import { useEffect, useState } from "react";

/**
 * Feb 26 2026 · Founder Onboarding Reel.
 *
 * A stylised HTML/CSS mock of the first-60-seconds new-founder
 * journey — signup → wizard → Three Little Birds generation → first plan section
 * rendering. Cycles through four beats on a 60-second loop
 * (15 seconds per scene). Zero external assets, pure CSS keyframes.
 *
 *   1. Signup card    — name + email + "Start free" CTA
 *   2. Wizard step    — offering picker + industry
 *   3. drafting    — animated progress bar assembling sections
 *   4. Plan rendered  — KPI band + 3 section headers appearing
 *
 * All motion respects prefers-reduced-motion — reduced viewers see
 * the "plan rendered" scene held statically since it's the most
 * information-dense frame + the payoff of the whole loop.
 */
const SCENES = ["signup", "wizard", "drafting", "plan"];
const SCENE_LABELS = {
  signup: "Sign up in under 30 seconds — no card",
  wizard: "Answer 6 quick questions about your business",
  drafting: "drafts your plan in real time",
  plan: "Your first plan section — live in 60 seconds",
};

function useReducedMotion() {
  const [reduced, setReduced] = useState(() => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  });
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const onChange = (e) => setReduced(e.matches);
    if (mq.addEventListener) mq.addEventListener("change", onChange);
    else if (mq.addListener) mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", onChange);
      else if (mq.removeListener) mq.removeListener(onChange);
    };
  }, []);
  return reduced;
}

export default function FounderOnboardingReel() {
  const reducedMotion = useReducedMotion();
  const PLAN_IDX = SCENES.indexOf("plan");
  const [scene, setScene] = useState(reducedMotion ? PLAN_IDX : 0);
  useEffect(() => {
    if (reducedMotion) { setScene(PLAN_IDX); return undefined; }
    const iv = setInterval(() => setScene((i) => (i + 1) % SCENES.length), 15000);
    return () => clearInterval(iv);
  }, [reducedMotion, PLAN_IDX]);

  return (
    <div
      className="relative w-full max-w-[520px] mx-auto"
      data-testid="founder-onboarding-reel"
      data-reduced-motion={reducedMotion ? "true" : "false"}
    >
      <style>{`
        @keyframes tlbOnbSignup   { 0%,20% {opacity:1} 25%,100% {opacity:0} }
        @keyframes tlbOnbWizard   { 0%,20% {opacity:0} 25%,45% {opacity:1} 50%,100% {opacity:0} }
        @keyframes tlbOnbDrafting { 0%,45% {opacity:0} 50%,70% {opacity:1} 75%,100% {opacity:0} }
        @keyframes tlbOnbPlan     { 0%,70% {opacity:0} 75%,100% {opacity:1} }
        @keyframes tlbOnbType     { 0% {width:0%} 60% {width:var(--w,100%)} 100% {width:var(--w,100%)} }
        @keyframes tlbOnbProgress { 0% {width:2%} 100% {width:100%} }
        @keyframes tlbOnbPulse    { 0%,100% {opacity:0.55} 50% {opacity:1} }
        @keyframes tlbOnbReveal   { 0%,15% {opacity:0; transform:translateY(6px)} 25%,100% {opacity:1; transform:translateY(0)} }
        .tlb-onb-scene { position:absolute; inset:0; animation-duration:60s; animation-iteration-count:infinite; animation-timing-function:ease-in-out; }
        .tlb-onb-type { display:block; overflow:hidden; white-space:nowrap; animation: tlbOnbType 4s steps(40, end) infinite; animation-delay:0.5s; }
        .tlb-onb-progress-fill { animation: tlbOnbProgress 8s ease-in-out infinite; }
        .tlb-onb-pulse-dot { animation: tlbOnbPulse 1.2s ease-in-out infinite; }
        .tlb-onb-reveal { animation: tlbOnbReveal 4s ease-out infinite; animation-fill-mode: both; }
        [data-testid="founder-onboarding-reel"][data-reduced-motion="true"] .tlb-onb-scene,
        [data-testid="founder-onboarding-reel"][data-reduced-motion="true"] .tlb-onb-type,
        [data-testid="founder-onboarding-reel"][data-reduced-motion="true"] .tlb-onb-progress-fill,
        [data-testid="founder-onboarding-reel"][data-reduced-motion="true"] .tlb-onb-pulse-dot,
        [data-testid="founder-onboarding-reel"][data-reduced-motion="true"] .tlb-onb-reveal {
          animation: none !important;
        }
        [data-testid="founder-onboarding-reel"][data-reduced-motion="true"] .tlb-onb-scene { opacity: 0; }
        [data-testid="founder-onboarding-reel"][data-reduced-motion="true"] [data-testid="founder-onboarding-reel-scene-plan"] { opacity: 1; }
        [data-testid="founder-onboarding-reel"][data-reduced-motion="true"] .tlb-onb-type { width: var(--w, 100%); }
        [data-testid="founder-onboarding-reel"][data-reduced-motion="true"] .tlb-onb-progress-fill { width: 100%; }
        @media (prefers-reduced-motion: reduce) {
          [data-testid="founder-onboarding-reel"] .tlb-onb-scene,
          [data-testid="founder-onboarding-reel"] .tlb-onb-type,
          [data-testid="founder-onboarding-reel"] .tlb-onb-progress-fill,
          [data-testid="founder-onboarding-reel"] .tlb-onb-pulse-dot,
          [data-testid="founder-onboarding-reel"] .tlb-onb-reveal {
            animation: none !important;
          }
        }
      `}</style>

      <div className="relative border-2 border-black bg-neutral-950 shadow-[8px_8px_0_0_rgba(0,0,0,0.9)]">
        <div className="flex items-center gap-1.5 px-3 py-2 bg-neutral-900 border-b border-neutral-800">
          <span className="w-2.5 h-2.5 rounded-full bg-rose-500" />
          <span className="w-2.5 h-2.5 rounded-full bg-amber-500" />
          <span className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
          <span
            className="ml-2 text-[10px] font-black uppercase tracking-[0.15em] text-neutral-400"
            data-testid="founder-onboarding-reel-chrome-title"
          >
            three-little-birds · get started
          </span>
        </div>
        <div className="relative bg-neutral-50 h-[340px] overflow-hidden">

          {/* Scene 1 · Signup card */}
          <div
            className="tlb-onb-scene p-6 flex flex-col items-center justify-center"
            style={{ animationName: "tlbOnbSignup" }}
            data-testid="founder-onboarding-reel-scene-signup"
          >
            <div className="text-[9px] uppercase tracking-[0.3em] text-[#7C5FB0] font-black mb-1">Step 1 of 4 · Sign up</div>
            <div className="font-display font-black text-xl tracking-tighter mb-4">Start free — no card needed</div>
            <div className="w-full max-w-[300px] space-y-2.5 bg-white border-2 border-neutral-900 p-4">
              <div>
                <div className="text-[9px] uppercase tracking-[0.2em] text-neutral-500 font-black mb-1">Your name</div>
                <div className="h-8 border border-neutral-300 bg-neutral-50 px-2 py-1.5 text-[11px] text-neutral-900 font-medium">
                  <span className="tlb-onb-type" style={{ "--w": "5.5em" }}>Alex Rivera</span>
                </div>
              </div>
              <div>
                <div className="text-[9px] uppercase tracking-[0.2em] text-neutral-500 font-black mb-1">Work email</div>
                <div className="h-8 border border-neutral-300 bg-neutral-50 px-2 py-1.5 text-[11px] text-neutral-900 font-medium">
                  <span className="tlb-onb-type" style={{ "--w": "9em", animationDelay: "1.2s" }}>alex@birdshop.co</span>
                </div>
              </div>
              <button
                type="button"
                className="w-full mt-1 h-9 bg-[#7C5FB0] hover:bg-[#6B4EA0] text-white text-[10px] uppercase tracking-[0.2em] font-black flex items-center justify-center gap-1.5"
              >
                Create free account →
              </button>
              <div className="text-[9px] text-center text-neutral-500 mt-1">
                Free trial · No card · Cancel anytime
              </div>
            </div>
          </div>

          {/* Scene 2 · Wizard step */}
          <div
            className="tlb-onb-scene p-4"
            style={{ animationName: "tlbOnbWizard" }}
            data-testid="founder-onboarding-reel-scene-wizard"
          >
            <div className="flex items-center justify-between mb-2">
              <div className="text-[9px] uppercase tracking-[0.3em] text-[#7C5FB0] font-black">Step 2 of 4 · Wizard</div>
              <span className="text-[9px] uppercase tracking-[0.15em] font-black text-neutral-500 border border-neutral-300 bg-white px-1.5 py-0.5">
                2 of 6
              </span>
            </div>
            <div className="font-display font-black text-lg tracking-tighter mb-3">What do you offer?</div>
            <div className="grid grid-cols-2 gap-2 mb-4">
              {[
                { t: "Products", d: "Physical or digital goods", sel: true },
                { t: "Services", d: "One-off or recurring", sel: false },
                { t: "Both", d: "Products + services", sel: false },
                { t: "Marketplace", d: "Match buyers ↔ sellers", sel: false },
              ].map((c) => (
                <div
                  key={c.t}
                  className={`border-2 p-2 text-[10px] transition-colors ${
                    c.sel
                      ? "border-[#7C5FB0] bg-[#F5F1FB] ring-2 ring-inset ring-[#7C5FB0]/40"
                      : "border-dashed border-neutral-300 bg-white"
                  }`}
                >
                  <div className={`font-black truncate ${c.sel ? "text-[#7C5FB0]" : ""}`}>
                    {c.t} {c.sel && <span className="text-[8px]">✓</span>}
                  </div>
                  <div className="text-neutral-500 mt-0.5 truncate">{c.d}</div>
                </div>
              ))}
            </div>
            <div className="text-[9px] uppercase tracking-[0.2em] text-neutral-500 font-black mb-1">Industry</div>
            <div className="h-9 border border-neutral-900 bg-white px-3 py-2 text-[11px] font-medium flex items-center">
              <span className="tlb-onb-type" style={{ "--w": "13em", animationDelay: "1.6s" }}>Coffee & specialty beverages</span>
            </div>
            <div className="mt-4 flex items-center gap-1.5">
              {[1, 2, 3, 4, 5, 6].map((n) => (
                <div
                  key={n}
                  className={`h-1 flex-1 ${n <= 2 ? "bg-[#7C5FB0]" : "bg-neutral-200"}`}
                />
              ))}
            </div>
          </div>

          {/* Scene 3 · drafting */}
          <div
            className="tlb-onb-scene p-6 flex flex-col justify-center"
            style={{ animationName: "tlbOnbDrafting" }}
            data-testid="founder-onboarding-reel-scene-drafting"
          >
            <div className="text-[9px] uppercase tracking-[0.3em] text-[#7C5FB0] font-black mb-1">Step 3 of 4 · Drafting</div>
            <div className="font-display font-black text-xl tracking-tighter mb-1">Your plan is generating…</div>
            <div className="text-[11px] text-neutral-500 mb-5">Anchored to your industry, geography, and Year 1 target</div>

            {/* Sections being assembled */}
            <div className="space-y-2.5 mb-5">
              {[
                { label: "Executive summary", state: "done" },
                { label: "Market analysis", state: "done" },
                { label: "Competitive landscape", state: "active" },
                { label: "Customer acquisition", state: "pending" },
                { label: "Infrastructure", state: "pending" },
                { label: "3-year revenue projection", state: "pending" },
              ].map((r) => (
                <div key={r.label} className="flex items-center gap-2 text-[11px]">
                  <span
                    className={`inline-flex items-center justify-center w-3.5 h-3.5 border ${
                      r.state === "done"
                        ? "bg-emerald-500 border-emerald-600 text-white"
                        : r.state === "active"
                          ? "bg-[#7C5FB0] border-[#6B4EA0] text-white tlb-onb-pulse-dot"
                          : "bg-white border-neutral-300"
                    }`}
                  >
                    {r.state === "done" ? (
                      <span className="text-[8px] font-black leading-none">✓</span>
                    ) : r.state === "active" ? (
                      <span className="text-[8px] font-black leading-none">·</span>
                    ) : null}
                  </span>
                  <span className={`font-medium ${r.state === "pending" ? "text-neutral-400" : "text-neutral-900"}`}>
                    {r.label}
                  </span>
                </div>
              ))}
            </div>

            {/* Progress bar */}
            <div className="h-2 bg-neutral-200 relative border border-neutral-300">
              <div
                className="tlb-onb-progress-fill absolute inset-y-0 left-0 bg-gradient-to-r from-[#7C5FB0] to-emerald-500"
                data-testid="founder-onboarding-reel-progress"
              />
            </div>
            <div className="mt-1.5 text-[9px] uppercase tracking-[0.2em] text-neutral-500 font-black text-right">
              ~45 seconds · powered by Three Little Birds
            </div>
          </div>

          {/* Scene 4 · Plan rendered */}
          <div
            className="tlb-onb-scene p-4"
            style={{ animationName: "tlbOnbPlan" }}
            data-testid="founder-onboarding-reel-scene-plan"
          >
            <div className="flex items-center justify-between mb-2">
              <div className="text-[9px] uppercase tracking-[0.3em] text-emerald-700 font-black">Step 4 of 4 · Live plan</div>
              <span className="text-[9px] uppercase tracking-[0.15em] font-black text-emerald-700 border border-emerald-400 bg-emerald-50 px-1.5 py-0.5">
                ● READY
              </span>
            </div>
            <div className="font-display font-black text-lg tracking-tighter mb-1">Birdshop Coffee · Business plan</div>
            <div className="text-[10px] text-neutral-500 mb-3">Coffee & specialty beverages · Portland, OR</div>

            {/* KPI band */}
            <div className="grid grid-cols-4 gap-0 border border-neutral-900 mb-3">
              {[
                { k: "Year 1 target", v: "$240k" },
                { k: "Age range", v: "25–44" },
                { k: "Gender", v: "All" },
                { k: "Geography", v: "PDX" },
              ].map((tile, i) => (
                <div
                  key={tile.k}
                  className={`px-2 py-2 text-center tlb-onb-reveal ${
                    i > 0 ? "border-l border-neutral-900" : ""
                  }`}
                  style={{ animationDelay: `${0.2 + i * 0.15}s` }}
                >
                  <div className="text-[8px] uppercase tracking-[0.2em] text-neutral-500 font-black mb-0.5">{tile.k}</div>
                  <div className="font-display font-black text-sm tracking-tighter">{tile.v}</div>
                </div>
              ))}
            </div>

            {/* Section headers appearing */}
            <div className="space-y-1.5">
              {[
                { label: "Executive summary", accent: "#7C5FB0", num: "" },
                { label: "Market analysis · TAM $2.4B", accent: "#0284C7", num: "01" },
                { label: "Competitive landscape · 4 rivals", accent: "#B45309", num: "02" },
                { label: "Customer acquisition · 5 tactics", accent: "#0D9488", num: "03" },
              ].map((sec, i) => (
                <div
                  key={sec.label}
                  className="flex items-center gap-2 border-l-4 bg-white pl-2 pr-2 py-1.5 tlb-onb-reveal"
                  style={{ borderLeftColor: sec.accent, animationDelay: `${1.0 + i * 0.25}s` }}
                  data-testid={`founder-onboarding-reel-plan-section-${i}`}
                >
                  {sec.num && (
                    <span
                      className="text-[9px] uppercase tracking-[0.2em] font-black tabular-nums"
                      style={{ color: sec.accent }}
                    >
                      {sec.num}
                    </span>
                  )}
                  <span className="text-[11px] font-black text-neutral-900 truncate">{sec.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="flex items-center justify-between px-3 py-2 bg-neutral-950 border-t border-neutral-800">
          <div
            className="text-[10px] uppercase tracking-[0.15em] font-black text-neutral-300"
            data-testid="founder-onboarding-reel-caption"
          >
            {reducedMotion ? "Static preview · reduced motion" : SCENE_LABELS[SCENES[scene]]}
          </div>
          <div className="flex items-center gap-1">
            {SCENES.map((s, i) => (
              <span
                key={s}
                className={`w-1.5 h-1.5 rounded-full transition-colors ${
                  i === scene ? "bg-emerald-400" : "bg-neutral-700"
                }`}
                data-testid={`founder-onboarding-reel-dot-${i}`}
              />
            ))}
          </div>
        </div>
      </div>
      <div className="text-center text-[10px] uppercase tracking-[0.2em] text-neutral-500 mt-3">
        Signup · Wizard · draft · Live plan — in 60 seconds
      </div>
    </div>
  );
}
